/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type {
  UnknownAction,
  ListenerEffectAPI,
  ThunkDispatch,
} from '@reduxjs/toolkit';
import { createListenerMiddleware } from '@reduxjs/toolkit';
import { queryWMSLayers } from '@opengeoweb/webmap';
import {
  fetchStacCatalogCollections,
  fetchStacCatalogRoot,
  stacCatalogCollectionToLayerProps,
  defaultEdrStyles,
  fetchEDRJson,
  normalizeEDRLabel,
} from '@opengeoweb/webmap-react';
import { serviceActions } from './reducer';
import type {
  FetchInitialServicesPayload,
  ServiceLayer,
  SetLayersForServicePayload,
  WebMapStateModuleState,
} from '../types';

export const serviceListener =
  createListenerMiddleware<WebMapStateModuleState>();

const fetchSTACLayers = async (serviceUrl: string): Promise<ServiceLayer[]> => {
  const collections = await fetchStacCatalogCollections(serviceUrl);

  return collections.map(stacCatalogCollectionToLayerProps);
};

interface EDRParameter {
  id?: string;
  label?: string;
  description?: string;
  observedProperty?: { label?: string | Record<string, string> };
  unit?: { label?: string; symbol?: { value?: string } };
}

interface EDRCollection {
  id: string;
  title?: string;
  path?: string[];
  groups?: string[];
  group?: string;
  parameter_names?: Record<string, EDRParameter>;
}

const fetchEDRLayers = async (serviceUrl: string): Promise<ServiceLayer[]> => {
  const baseUrl = serviceUrl.endsWith('/')
    ? serviceUrl.slice(0, -1)
    : serviceUrl;
  const data = await fetchEDRJson<{ collections?: EDRCollection[] }>(
    `${baseUrl}/collections`,
  );
  return (data.collections ?? []).flatMap((collection) =>
    Object.entries(collection.parameter_names ?? {}).map(
      ([parameterId, parameter]) =>
        ({
          name: parameterId,
          title: parameter.label ?? parameterId,
          leaf: true,
          path: [
            ...(collection.path ??
              collection.groups ??
              (collection.group ? [collection.group] : [])),
            collection.title ?? collection.id,
          ],
          keywords: parameter.observedProperty?.label
            ? [
                normalizeEDRLabel(
                  parameter.observedProperty.label,
                  parameter.label ?? parameterId,
                ),
              ]
            : [],
          edrCollectionId: collection.id,
          edrParameter: parameter,
          styles: defaultEdrStyles,
        }) as ServiceLayer,
    ),
  );
};

const dispatchServiceLayers = (
  service: SetLayersForServicePayload,
  layers: ServiceLayer[],
  listenerApi: ListenerEffectAPI<
    WebMapStateModuleState,
    ThunkDispatch<WebMapStateModuleState, unknown, UnknownAction>,
    unknown
  >,
): void => {
  listenerApi.dispatch(
    serviceActions.serviceSetLayers({
      ...service,
      layers,
    }),
  );
};

const fetchSTACService = async (
  service: SetLayersForServicePayload,
  listenerApi: ListenerEffectAPI<
    WebMapStateModuleState,
    ThunkDispatch<WebMapStateModuleState, unknown, UnknownAction>,
    unknown
  >,
): Promise<void> => {
  const [root, layers] = await Promise.all([
    fetchStacCatalogRoot(service.serviceUrl),
    fetchSTACLayers(service.serviceUrl),
  ]);

  dispatchServiceLayers(
    {
      ...service,
      name: service.name || root.title || root.id || service.id,
      abstract: service.abstract || root.description,
      type: 'STAC',
    },
    layers,
    listenerApi,
  );
};

export const fetchService = async (
  service: SetLayersForServicePayload,
  listenerApi: ListenerEffectAPI<
    WebMapStateModuleState,
    ThunkDispatch<WebMapStateModuleState, unknown, UnknownAction>,
    unknown
  >,
): Promise<void> => {
  if (service.type === 'EDR') {
    try {
      const layers = await fetchEDRLayers(service.serviceUrl);
      dispatchServiceLayers(service, layers, listenerApi);
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.warn(error.message);
      }
      dispatchServiceLayers(service, [], listenerApi);
    }
    return;
  }

  if (service.type === 'STAC') {
    await fetchSTACService(service, listenerApi);
    return;
  }

  try {
    const layers = await queryWMSLayers(service.serviceUrl);

    dispatchServiceLayers(service, layers, listenerApi);
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.warn(error.message);
    }
  }
};

export const fetchInitialServices = async (
  payload: FetchInitialServicesPayload,
  listenerApi: ListenerEffectAPI<
    WebMapStateModuleState,
    ThunkDispatch<WebMapStateModuleState, unknown, UnknownAction>,
    unknown
  >,
): Promise<void> => {
  if (!payload?.services) {
    return;
  }
  try {
    await Promise.all(
      payload.services.map(async ({ url, ...service }) =>
        fetchService(
          {
            ...service,
            serviceUrl: url,
          } as SetLayersForServicePayload,
          listenerApi,
        ),
      ),
    );
  } catch (error) {
    console.warn(error);
  }
};

serviceListener.startListening({
  actionCreator: serviceActions.fetchInitialServices,
  effect: async ({ payload }, listenerApi) => {
    listenerApi.cancelActiveListeners();

    await fetchInitialServices(payload, listenerApi);
  },
});
